import { differenceInDays } from 'date-fns';

import { GoalThreshold } from '../../coaching-base/config';
import {
    CommitmentApprovalDto,
    CommitmentKpi,
    EntityType,
    GoalStatus,
} from '../../coaching-base/general';
import { TranslationKeyMap } from '../coaching-commitment.i18n';

import { GoalCategoryItemView } from './goal-category-item-view.model';

const statusTranslationMap: { [key in GoalStatus]: string } = {
    [GoalStatus.DRAFT]: TranslationKeyMap._9476_DRAFT,
    [GoalStatus.PRE_APPROVAL]: TranslationKeyMap._9791_PENDING_APPROVAL,
    [GoalStatus.ACTIVE]: TranslationKeyMap._125_ACTIVE,
    [GoalStatus.UPDATING]: TranslationKeyMap._9791_PENDING_APPROVAL,
    [GoalStatus.PENDING_CLOSE]: TranslationKeyMap._7441_PENDING_CLOSE,
    [GoalStatus.CLOSED]: TranslationKeyMap._7440_CLOSED,
    [GoalStatus.DELETED]: TranslationKeyMap._9496_DELETED,
};

const statusColorMap: { [key in GoalStatus]: string } = {
    [GoalStatus.DRAFT]: '#505050',
    [GoalStatus.PRE_APPROVAL]: '#9747ff',
    [GoalStatus.ACTIVE]: '#137e0f',
    [GoalStatus.UPDATING]: '#9747ff',
    [GoalStatus.PENDING_CLOSE]: '#9747ff',
    [GoalStatus.CLOSED]: '#bababa',
    [GoalStatus.DELETED]: null,
};

const statusIconMap: { [key in GoalStatus]: string } = {
    [GoalStatus.DRAFT]: 'draft-icon',
    [GoalStatus.PRE_APPROVAL]: 'pending-icon',
    [GoalStatus.ACTIVE]: 'thumb-up-outline',
    [GoalStatus.UPDATING]: 'pending-icon',
    [GoalStatus.PENDING_CLOSE]: 'pending-icon',
    [GoalStatus.CLOSED]: 'close-box-outline',
    [GoalStatus.DELETED]: null,
};

/** @internal only exported for testing */
export function determineStatusLabel(
    statusId: GoalStatus,
    disputedByMe: boolean,
    disputedByOtherParticipant: boolean,
    isOriginalGoal: boolean,
    hasDraft: boolean,
    achievementMissed: boolean,
    existsInApproval: boolean,
    isPending: boolean,
): string {
    let result = statusTranslationMap[statusId];
    const disputed = disputedByMe || disputedByOtherParticipant;

    if (isOriginalGoal && disputed) {
        result = TranslationKeyMap._9792_BEING_DISPUTED;
    } else if (
        !isOriginalGoal &&
        disputedByMe &&
        statusId === GoalStatus.PENDING_CLOSE
    ) {
        result = TranslationKeyMap._9812_DISPUTED_BY_ME__PENDING_CLOSE_;
    } else if (!isOriginalGoal && disputedByMe) {
        result = TranslationKeyMap._9793_DISPUTED_BY_ME;
    } else if (
        !isOriginalGoal &&
        disputedByOtherParticipant &&
        statusId === GoalStatus.PENDING_CLOSE
    ) {
        result = TranslationKeyMap._9811_DISPUTED__PENDING_CLOSE_;
    } else if (!isOriginalGoal && disputedByOtherParticipant) {
        result = TranslationKeyMap._8703_DISPUTED;
    } else if (isOriginalGoal && hasDraft && statusId === GoalStatus.ACTIVE) {
        result = TranslationKeyMap._9814_ACTIVE__WITH_A_DRAFT_VERSION_;
    } else if (!isOriginalGoal && statusId === GoalStatus.ACTIVE) {
        result = TranslationKeyMap._9816_DRAFT__WITH_AN_ACTIVE_VERSION_;
    } else if (
        !isOriginalGoal &&
        statusId === GoalStatus.PENDING_CLOSE &&
        !existsInApproval
    ) {
        result = TranslationKeyMap._9810_DRAFT__PENDING_CLOSE_;
    } else if (achievementMissed && !isPending) {
        result = TranslationKeyMap._9809_MISSED;
    }
    return result;
}

/** @internal only exported for testing */
export function determineColors(
    statusId: GoalStatus,
    disputedByMe: boolean,
    disputedByOtherParticipant: boolean,
    isOriginalGoal: boolean,
    achievementMissed: boolean,
    existsInApproval: boolean,
    isPending: boolean,
): { statusColor: string; statusIcon: string } {
    let statusColor = statusColorMap[statusId];
    let statusIcon = statusIconMap[statusId];
    const disputed = disputedByMe || disputedByOtherParticipant;

    if (disputed) {
        statusColor = '#ce2521';
        statusIcon = 'thumb-down-outline';
    } else if (
        !isOriginalGoal &&
        !disputed &&
        (statusId === GoalStatus.ACTIVE ||
            (statusId === GoalStatus.PENDING_CLOSE && !existsInApproval))
    ) {
        statusColor = statusColorMap[GoalStatus.DRAFT];
        statusIcon = statusIconMap[GoalStatus.DRAFT];
    } else if (achievementMissed && !isPending) {
        statusColor = '#ce2521';
        statusIcon = 'alert';
    }
    return { statusColor, statusIcon };
}

// TODO sync and consolidate with commitment-summary.transformer->determineDaysToAchievement()
// TODO change CommitmentKpi so that startDate and endDate are also available
/** @internal only exported for testing */
export function determineDaysToAchievement(
    estimatedCompletionDate: Date,
    daysActive: number,
    statusId: GoalStatus,
    goalThresholds: GoalThreshold[],
): {
    daysToAchievementIcon: string;
    daysToAchievementColor: string;
    daysToAchievementLeftLabel: string;
    daysToAchievementLabel: string;
    daysToAchievement: number;
    daysToAchievementAbs: number;
    daysActive: number;
    achievementMissed: boolean;
} {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    let daysToAchievement = Math.abs(
        differenceInDays(now, estimatedCompletionDate),
    );
    if (estimatedCompletionDate < now) {
        daysToAchievement *= -1;
    }

    let daysToAchievementLeftLabel =
        TranslationKeyMap._9807_DAYS_BEFORE_EXP__DATE;
    let daysToAchievementLabel: string;
    if (statusId === GoalStatus.CLOSED) {
        daysToAchievementLabel = TranslationKeyMap._7440_CLOSED;
        daysToAchievement = null;
    } else if (
        statusId === GoalStatus.DRAFT ||
        statusId === GoalStatus.DELETED
    ) {
        daysToAchievement = null;
    } else {
        if (daysToAchievement < 0) {
            daysToAchievementLeftLabel =
                TranslationKeyMap._9808_DAYS_AFTER_EXP__DATE;
            daysToAchievementLabel = TranslationKeyMap._9809_MISSED;
        }
        if (daysToAchievement === 0) {
            daysToAchievementLabel = TranslationKeyMap._9569_TODAY;
        }
        if (daysToAchievement == null || isNaN(daysToAchievement)) {
            daysToAchievementLabel = '-';
        }
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    let goalThreshold = {} as GoalThreshold;
    if (daysToAchievement != null) {
        goalThreshold =
            goalThresholds.find((e) => daysToAchievement < e.threshold) ||
            goalThreshold;
    }

    return {
        daysToAchievementIcon: goalThreshold.icon,
        daysToAchievementColor: goalThreshold.color,
        daysToAchievementLeftLabel,
        daysToAchievementLabel,
        daysToAchievement,
        daysToAchievementAbs:
            daysToAchievement != null ? Math.abs(daysToAchievement) : null,
        daysActive,
        achievementMissed: daysToAchievement != null && daysToAchievement < 0,
    };
}

const pendingStatuses: GoalStatus[] = [
    GoalStatus.PRE_APPROVAL,
    GoalStatus.UPDATING,
    GoalStatus.PENDING_CLOSE,
];

export function goalCategoryItemViewTransformer(
    items: CommitmentKpi[],
    goalThresholds: GoalThreshold[],
    approval: CommitmentApprovalDto,
    authUserId: number,
    areFromOtherCommitments = false,
): GoalCategoryItemView[] {
    const gThresholds = (goalThresholds ? [...goalThresholds] : []).sort(
        (a, b) => a.threshold - b.threshold,
    );

    let disputedDiffItemIds: string[] = [];
    const disputedGoalsIds: number[] = [];
    if (approval) {
        disputedDiffItemIds = approval.state.items
            .filter((e) => e.approved === false)
            .map((e) => e.diffItemId);

        /* draft updates of existing goals hold the same goal id until the changes are approved,
        but only the diffItemIds of the original goal and the draft update are different.
        so a goalId map is used to ensure the original is also flagged when the draft update is disputed */
        const apDiffItemIdToGoalIdMap = {};
        approval.diff.diffItems
            .filter(
                (e) =>
                    e.entityTypeId === EntityType.Goal &&
                    e.entityId != null &&
                    e.entityId !== 0,
            )
            .forEach((e) => {
                apDiffItemIdToGoalIdMap[e.diffItemId] = e.entityId;
            });
        disputedDiffItemIds.forEach((e) => {
            // eslint-disable-next-line no-prototype-builtins
            if (apDiffItemIdToGoalIdMap.hasOwnProperty(e)) {
                disputedGoalsIds.push(apDiffItemIdToGoalIdMap[e]);
            }
        });
    }

    return items.map<GoalCategoryItemView>((e) => {
        const disputed =
            (approval != null &&
                e.diffItemId &&
                disputedDiffItemIds.includes(e.diffItemId)) ||
            disputedGoalsIds.includes(e.goalId);
        const disputedByMe = disputed && approval.targetId === authUserId;
        const disputedByOtherParticipant =
            disputed && approval.targetId !== authUserId;
        const isOriginalWithDraft =
            e.isOriginalGoal &&
            items.some((ee) => !ee.isOriginalGoal && ee.goalId === e.goalId);
        const isOriginalWithDraftPendingApproval =
            e.isOriginalGoal &&
            approval != null &&
            approval.approved == null &&
            items.some(
                (ee) =>
                    !ee.isOriginalGoal &&
                    ee.goalId === e.goalId &&
                    pendingStatuses.includes(ee.statusId),
            );

        const daysToAchievement = determineDaysToAchievement(
            e.estimatedCompletionDate,
            e.activeGoalDays,
            e.statusId,
            gThresholds,
        );
        const isPending = pendingStatuses.includes(e.statusId);

        const statusLabel = determineStatusLabel(
            e.statusId,
            disputedByMe,
            disputedByOtherParticipant,
            e.isOriginalGoal,
            isOriginalWithDraft,
            daysToAchievement.achievementMissed,
            !!e.diffItemId,
            isPending,
        );

        const { statusColor, statusIcon } = determineColors(
            e.statusId,
            disputedByMe,
            disputedByOtherParticipant,
            e.isOriginalGoal,
            daysToAchievement.achievementMissed,
            !!e.diffItemId,
            isPending,
        );

        return {
            goal: e,
            disputed,
            statusLabel,
            statusColor,
            statusIcon,
            isOriginalWithDraft,
            isOriginalWithDraftPendingApproval,
            ...daysToAchievement,
            isFromOtherCommitment: areFromOtherCommitments,
        };
    });
}
